import {
  ICellPositions,
  ICreateBayGridProps,
  ISvgWithSize,
} from "../../types/ICreateBayGridBaseProps";
import { IRowPattern, ISlotCombinedPattern } from "@tedivo/tedivo-bay-grid-pure";
import { roundDec, sanitizeString } from "@tedivo/tedivo-pure-helpers";

import { ILidDataWithValidation } from "@tedivo/tedivo-ovd-validators";
import createGridPart from "./createGridPart";

/**
 * Generates a SVG Grid (Above & Below) with dimensions
 * @returns ISvgWithSize {svg, width, height}
 */
export default function createBayGrid<U>({
  aboveTiersArray,
  belowTiersArray,
  belowGridSeparation = 0,
  lidData,
  aboveTcgLabelsByRow,
  belowTcgLabelsByRow,
  ...commonGridOptions
}: ICreateBayGridProps<U>): ISvgWithSize {
  const svgGroup = document.createElementNS("http://www.w3.org/2000/svg", "g");
  let height = 0,
    aboveHeight = 0,
    width = 0;

  const symbols: { [id: string]: SVGSymbolElement } = {};
  const cellPositions: ICellPositions = {};

  if (aboveTiersArray.length) {
    const aboveSvg = createGridPart({
      ...commonGridOptions,
      tiersArray: aboveTiersArray.slice().sort(sortNumericDesc),
      displayNumbers: { left: true, right: true, top: true },
      tcgLabelsByRow: aboveTcgLabelsByRow,
    });
    height = aboveSvg.height;
    aboveHeight = aboveSvg.height;
    width = aboveSvg.width;
    svgGroup.appendChild(aboveSvg.svgGroup);
    Object.assign(cellPositions, aboveSvg.cellPositions);
    Object.assign(symbols, aboveSvg.symbols);
  }

  if (belowTiersArray.length) {
    const belowSvg = createGridPart({
      ...commonGridOptions,
      tiersArray: belowTiersArray.slice().sort(sortNumericDesc),
      displayNumbers: { left: true, right: true, bottom: true },
      tcgLabelsByRow: belowTcgLabelsByRow,
    });
    belowSvg.svgGroup.setAttribute(
      "transform",
      `translate(0, ${height > 0 ? height + belowGridSeparation : 0})`,
    );
    svgGroup.appendChild(belowSvg.svgGroup);

    if (height > 0) {
      Object.assign(
        cellPositions,
        (Object.keys(belowSvg.cellPositions) as ISlotCombinedPattern[]).reduce((acc, pos) => {
          acc[pos] = {
            x: belowSvg.cellPositions[pos].x,
            y: belowSvg.cellPositions[pos].y + height + belowGridSeparation,
          };
          return acc;
        }, {} as ICellPositions),
      );
    } else {
      Object.assign(cellPositions, belowSvg.cellPositions);
    }

    Object.assign(symbols, belowSvg.symbols);
    height += belowGridSeparation + belowSvg.height;
  }

  const nodesOptions = commonGridOptions.nodesOptions;

  const lidsPath = drawLids({
    lidData,
    yPos: aboveHeight ? aboveHeight + (belowGridSeparation || 0) * 0.5 : 0,
    containerWidth: commonGridOptions.containerWidth,
    lidHeight: commonGridOptions.lidHeight,
    cellPositions,
    lidBorderColor: nodesOptions.lidBorderColor,
    lidBackgroundColor: nodesOptions.lidBackgroundColor,
    lidDangerBorderColor: nodesOptions.lidDangerBorderColor,
    lidDangerBackgroundColor: nodesOptions.lidDangerBackgroundColor,
    lidWarningBorderColor: nodesOptions.lidWarningBorderColor,
    lidWarningBackgroundColor: nodesOptions.lidWarningBackgroundColor,
    lidTextColor: nodesOptions.emphasizeLineColor,
    cellsAreSeparated:
      commonGridOptions.cellSeparation !== undefined && commonGridOptions.cellSeparation > 0,
  });

  if (lidsPath) svgGroup.appendChild(lidsPath);

  return {
    svgGroup,
    width,
    height,
    symbols,
    cellPositions,
  };
}

function sortNumericDesc(a: number | string, b: number | string): number {
  return Number(b) - Number(a);
}

function drawLids({
  lidData,
  yPos,
  containerWidth,
  lidHeight,
  cellPositions,
  lidBorderColor,
  lidBackgroundColor,
  lidDangerBorderColor,
  lidDangerBackgroundColor,
  lidWarningBorderColor,
  lidWarningBackgroundColor,
  lidTextColor,
  cellsAreSeparated,
}: {
  lidData: Array<ILidDataWithValidation> | undefined;
  yPos: number;
  containerWidth: number;
  lidHeight: number;
  cellPositions: ICellPositions;
  lidBorderColor: string | undefined;
  lidBackgroundColor: string | undefined;
  lidDangerBorderColor: string | undefined;
  lidDangerBackgroundColor: string | undefined;
  lidWarningBorderColor: string | undefined;
  lidWarningBackgroundColor: string | undefined;
  lidTextColor: string | undefined;
  cellsAreSeparated: boolean;
}): SVGGElement | null {
  if (lidData === undefined || lidData === null || lidData.length === 0) return null;

  const LIDHEIGHT = lidHeight;

  const OVERLAP_WIDTH = roundDec(containerWidth * 0.15, 3);
  const OVERLAP_HEIGHT = roundDec(LIDHEIGHT * (cellsAreSeparated ? 0.42 : 0.5), 3);

  const rowPositions: { [row: IRowPattern]: number } = {};
  (Object.keys(cellPositions) as ISlotCombinedPattern[]).forEach((pos) => {
    const [row] = pos.split("|");
    rowPositions[row as IRowPattern] = cellPositions[pos].x;
  });

  const svgLidsPath = document.createElementNS("http://www.w3.org/2000/svg", "g");

  const lidDataSorted = lidData
    .slice()
    .sort((a, b) => (rowPositions[a.portIsoRow] < rowPositions[b.portIsoRow] ? -1 : 1));

  const maxLidIndex = lidDataSorted.length - 1;

  // Important. Because of sorting, we draw from Left to Right. PrevLid will be at the left always.
  lidDataSorted.forEach((lid, idx) => {
    const { svgPath, svgLabel } = addLidSvgPathInfo({
      lid,
      yOffset: yPos - LIDHEIGHT * 0.5,
      prevLid: idx > 0 ? lidDataSorted[idx - 1] : undefined,
      nextLid: idx < maxLidIndex ? lidDataSorted[idx + 1] : undefined,
    });

    svgLidsPath.appendChild(svgPath);
    svgLidsPath.appendChild(svgLabel);
  });

  return svgLidsPath;

  function addLidSvgPathInfo({
    lid,
    yOffset,
    prevLid,
    nextLid,
  }: {
    lid: ILidDataWithValidation;
    yOffset: number;
    prevLid?: ILidDataWithValidation;
    nextLid?: ILidDataWithValidation;
  }): { svgPath: SVGPathElement; svgLabel: SVGTextElement } {
    const svgLidPath = document.createElementNS("http://www.w3.org/2000/svg", "path");

    const left = rowPositions[lid.portIsoRow];
    const right = rowPositions[lid.starboardIsoRow];

    const leftTop = lid.overlapPort
      ? `M${left},${OVERLAP_HEIGHT + yOffset} h-${OVERLAP_WIDTH} v-${OVERLAP_HEIGHT}`
      : prevLid?.overlapStarboard
      ? `M${left + OVERLAP_WIDTH},${yOffset}`
      : `M${left},${yOffset}`;

    const rightTop = lid.overlapStarboard
      ? `L${
          right + containerWidth + OVERLAP_WIDTH
        },${yOffset} v${OVERLAP_HEIGHT} h-${OVERLAP_WIDTH} `
      : nextLid?.overlapPort
      ? `L${right + containerWidth - OVERLAP_WIDTH},${yOffset} V${
          yOffset + LIDHEIGHT - OVERLAP_HEIGHT
        } h${OVERLAP_WIDTH}`
      : `L${right + containerWidth},${yOffset}`;

    const rightBottom = `L${right + containerWidth},${yOffset + LIDHEIGHT}`;

    const leftBottom = prevLid?.overlapStarboard
      ? `L${left},${yOffset + LIDHEIGHT} v-${OVERLAP_HEIGHT} h${OVERLAP_WIDTH}`
      : `L${left},${yOffset + LIDHEIGHT}`;

    const d = [leftTop, rightTop, rightBottom, leftBottom, "z"].join("\n");

    const borderColor =
      lid.notValid === "danger"
        ? lidDangerBorderColor || "red"
        : lid.notValid === "warning"
        ? lidWarningBorderColor || "orange"
        : lidBorderColor || "black";
    const backgroundColor =
      lid.notValid === "danger"
        ? lidDangerBackgroundColor || "red"
        : lid.notValid === "warning"
        ? lidWarningBackgroundColor || "orange"
        : lidBackgroundColor || "black";

    svgLidPath.setAttribute("d", d);
    svgLidPath.setAttribute("fill", backgroundColor);
    svgLidPath.setAttribute("fill-opacity", "0.5");
    svgLidPath.setAttribute("stroke", borderColor);

    const svgTitle = document.createElementNS("http://www.w3.org/2000/svg", "title");
    svgTitle.textContent = sanitizeString(lid.label);
    svgLidPath.appendChild(svgTitle);
    svgLidPath.setAttribute("part", "svg-lid");
    svgLidPath.setAttribute("role", "figure");

    const svgLabel = document.createElementNS("http://www.w3.org/2000/svg", "text");
    svgLabel.setAttribute("x", `${left + (right + containerWidth - left) * 0.5}`);
    svgLabel.setAttribute("y", `${yOffset + LIDHEIGHT * 0.55}`);
    svgLabel.setAttribute("text-anchor", "middle");
    svgLabel.setAttribute("dominant-baseline", "middle");
    svgLabel.setAttribute("font-size", `${Math.min(LIDHEIGHT * 0.85, 14)}`);
    svgLabel.setAttribute("fill", lidTextColor || "black");
    svgLabel.setAttribute("part", "svg-lid-label");
    svgLabel.setAttribute("role", "text");
    svgLabel.setAttribute("pointer-events", "none");
    svgLabel.setAttribute("font-family", "Arial, Helvetica, sans-serif");
    svgLabel.textContent = sanitizeString(lid.label);

    return { svgPath: svgLidPath, svgLabel };
  }
}
